// Chance de evadir exibida nas telas de evasão. O backend devolve em `bandRates` a curva
// calibrada (P de evadir no horizonte dado o score) já resolvida por score, com intervalo de
// 95% e o que foi observado naquele score; aqui só se formata. `bandRates` é null quando não
// há curva — nesse caso `probabilityStatus` diz o porquê e traz a taxa média da instituição.

const pct = (value) => `${Math.round(Number(value) * 100)}%`;

// Probabilidade do aluno (0–1) ou null. Sem fallback para a taxa da faixa: a faixa é evasão
// observada (evidência), não a chance estimada do aluno.
export const probabilityForStudent = (bandRates, student) => {
  const value = bandRates?.byScore?.[String(student?.matchedCriteriaCount)];
  return value == null ? null : Number(value);
};

// Percentual inteiro: a precisão real do número não sustenta casas decimais.
export const formatChance = (probability) => (probability == null ? null : pct(probability));

// "6 meses" a partir do horizonte em dias do backtest.
export const horizonLabel = (horizonDays) => {
  const months = Math.max(1, Math.round(Number(horizonDays || 180) / 30));
  return months === 1 ? "1 mês" : `${months} meses`;
};

// Texto do tooltip do percentual: intervalo de confiança + evidência observada no score.
export const chanceTooltip = (bandRates, student) => {
  const score = student?.matchedCriteriaCount;
  const detail = bandRates?.byScoreDetail?.[String(score)];
  if (!detail) {
    return undefined;
  }

  const range = `Entre ${pct(detail.low)} e ${pct(detail.high)} com 95% de confiança`;
  const evidence = detail.observedCount > 0
    ? `${detail.observedDropped} de ${detail.observedCount} alunos com score ${score} evadiram nos ciclos medidos`
    : `nenhum aluno com score ${score} nos ciclos medidos, valor projetado pela curva`;

  return `${range} · ${evidence}`;
};

// Estimativa apoiada (toda ou em parte) em histórico importado, não em ciclos reais de validação.
export const isRetroEstimate = (bandRates) => !!bandRates && bandRates.source !== "backtest";

// Uma frase sobre como a chance foi calculada, para rodapés e tooltips de cabeçalho.
export const describeCalibration = (bandRates) => {
  if (!bandRates) {
    return "";
  }

  const horizon = horizonLabel(bandRates.horizonDays);
  const cycles = Number(bandRates.cycles || 0);
  const sample = cycles > 0
    ? ` sobre ${cycles} ${cycles === 1 ? "ciclo medido" : "ciclos medidos"} (${bandRates.population} alunos, ${bandRates.positives} evasões)`
    : "";
  const retro = isRetroEstimate(bandRates)
    ? " Estimativa retroativa, baseada no histórico importado; será refinada pelos ciclos de validação reais."
    : "";

  return `A chance de evadir é a probabilidade estimada de o aluno evadir em ${horizon}, calibrada pelo score${sample}.${retro}`;
};

// Por que ainda não há chance por aluno, quando já existe ciclo medido mas não há curva.
export const describeMissingCalibration = (probabilityStatus) => {
  if (!probabilityStatus) {
    return "";
  }

  const horizon = horizonLabel(probabilityStatus.horizonDays);
  const base = probabilityStatus.baseRate != null
    ? ` Evasão média observada em ${horizon}: ${pct(probabilityStatus.baseRate)}.`
    : "";

  if (probabilityStatus.status === "no-signal") {
    return `Nos ciclos medidos o score ainda não distinguiu quem evadiu, então a chance por aluno não é exibida.${base}`;
  }

  return `Ainda não há histórico suficiente para estimar a chance de evadir por aluno (${probabilityStatus.positives} evasões em ${probabilityStatus.population} alunos medidos).${base}`;
};
